import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as s from './ContentSlidesList.css';
import { faClose, faFiles } from '@fortawesome/pro-solid-svg-icons';
import { F1 } from '@client/new/helpers/types';
import { useRef, useState, ReactNode, useEffect } from 'react';
import { Col, Row } from '@design-system/components/grid/grid';
import { extrasVM } from '../Extras.page';
import { alignRight } from './form.css';
import { Button } from '@design-system/components/button/button';
import { Typography } from '@design-system/components/typography/typography';
import { useContentFormVM } from '../viewModel/extrasForm.viewModel';
import { ContentTree } from '../model/extras.domain';
import { validateImage } from '../viewModel/extras.validation';

export interface UseSlidesReorderProps {
  slides: ContentTree[];
  isLoading: boolean;
  onReorder?: (newOrder: string[]) => void;
}

export interface UseSlidesReorderReturn {
  orderedSlides: ContentTree[];
  localOrder: string[];
  handleReorder: (activeId: string, overId: string) => void;
  cachedSlides: ContentTree[];
}

export const useSlidesReorder = ({
  slides,
  isLoading,
  onReorder,
}: UseSlidesReorderProps): UseSlidesReorderReturn => {
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const [cachedSlides, setCachedSlides] = useState<ContentTree[]>([]);

  useEffect(() => {
    if (!isLoading) {
      setLocalOrder(slides.map((slide) => slide.id));
      setCachedSlides(slides);
    }
  }, [slides.length, isLoading]);

  const handleReorder = (activeId: string, overId: string) => {
    if (activeId === overId || isLoading) return;

    const oldIndex = localOrder.indexOf(activeId);
    const newIndex = localOrder.indexOf(overId);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = [...localOrder];
    newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, activeId);

    setLocalOrder(newOrder);
    onReorder?.(newOrder);
  };

  const slidesToUse = isLoading ? cachedSlides : slides;
  const orderedSlides = localOrder
    .map((id) => slidesToUse.find((slide) => slide.id === id))
    .filter((slide): slide is ContentTree => slide !== undefined);

  return {
    orderedSlides,
    localOrder,
    handleReorder,
    cachedSlides,
  };
};

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type TemplatesListProps = {
  slides: ContentTree[];
  onRemove?: F1<string, void>;
  onReorder?: F1<string[], void>;
};

type DragDropWrapperProps = {
  children: ReactNode;
  onDrop: (files: FileList) => void;
  disabled?: boolean;
  className?: string;
};

export const DragDropWrapper = ({
  children,
  onDrop,
  disabled = false,
  className = '',
}: DragDropWrapperProps) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragEnter = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    const hasFiles = e.dataTransfer.types.includes('Files');
    if (hasFiles) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    const hasFiles = e.dataTransfer.types.includes('Files');
    e.dataTransfer.dropEffect = hasFiles ? 'copy' : 'none';
  };

  const handleDrop = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const imageFiles = Array.from(files).filter((file) =>
        file.type.startsWith('image/'),
      );
      if (imageFiles.length > 0) {
        const dt = new DataTransfer();
        imageFiles.forEach((file) => dt.items.add(file));
        onDrop(dt.files);
      }
    }
  };

  return (
    <div
      className={`${s.dragDropWrapper} ${className}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}
      {isDragOver && (
        <div className={s.dragOverlay}>
          <div className={s.dragOverlayContent}>Drop images here</div>
        </div>
      )}
    </div>
  );
};

const SlideContent = ({
  id,
  isUploading,
}: {
  id: string;
  isUploading: boolean;
}) => {
  const formVM = useContentFormVM(id);
  const isItemUploading =
    isUploading && formVM.values.file && !formVM.values.url;

  const imageStyle = {
    opacity: isItemUploading ? 0.7 : 1,
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    objectPosition: 'center' as const,
  };

  return (
    <>
      {isItemUploading && (
        <div className={s.loadingOverlay}>
          <div className={s.spinner} />
        </div>
      )}

      {formVM.values.file && (
        <img
          src={URL.createObjectURL(formVM.values.file)}
          alt={id}
          style={imageStyle}
        />
      )}
      {formVM.values.url && (
        <img src={formVM.values.url} alt={id} style={imageStyle} />
      )}
    </>
  );
};

const SortableTemplateItem = ({
  id,
  onRemove,
  isUploading,
}: {
  id: string;
  onRemove?: F1<string, void>;
  isUploading: boolean;
}) => {
  const formVM = useContentFormVM(id);
  const isItemUploading =
    isUploading && formVM.values.file && !formVM.values.url;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    // Wyłączamy animacje layout changes
    animateLayoutChanges: () => false,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition, // Bez transition podczas drag
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`${s.card} ${isDragging ? s.cardDragging : ''}`}
    >
      {/* Obszar do draggowania - bez przycisków */}
      <div
        {...listeners}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
        }}
      />

      <SlideContent id={id} isUploading={isUploading} />

      {onRemove && !isItemUploading && (
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove(id);
          }}
          className={s.cardClose}
          style={{
            zIndex: 10,
          }}
        >
          <FontAwesomeIcon icon={faClose} />
        </Button>
      )}
    </div>
  );
};

const DragOverlayItem = ({ id }: { id: string }) => {
  return (
    <div className={s.dragOverlayCard}>
      <SlideContent id={id} isUploading={false} />
    </div>
  );
};

export const ContentSlidesList = (p: TemplatesListProps) => {
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const extras = extrasVM.useValue();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const isLoading = extras.savingSlides?.has(extras.currentId) || false;

  const { orderedSlides, handleReorder } = useSlidesReorder({
    slides: p.slides,
    isLoading,
    onReorder: p.onReorder,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
        delay: 0,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      handleReorder(active.id as string, over.id as string);
    }
  };

  const handleAddSlides = (files: FileList) => {
    if (files.length > 0 && !isLoading) {
      Array.from(files).forEach((file) => {
        validateImage(file, 1125, 2436).then((v) => {
          console.log('Image validation result:', v);
          setErrorMessage(null);
          if (v.isValid) {
            extras.dispatch('AddSlide', file);
          } else {
            setErrorMessage(
              'One of the images has invalid dimensions. Minimum dimensions are 1125 x 2436px.',
            );
          }
        });
      });
    }
  };

  return (
    <DragDropWrapper onDrop={handleAddSlides} disabled={isLoading}>
      <Col>
        <Row className={s.gridMaxContent}>
          <Col>
            <Typography variant="body" size="large" fontWeight="medium">
              Slides
            </Typography>
            <Typography variant="body" fontWeight="normal">
              Upload portrait images. Minimum image dimensions: 1125 x 2436px.
              Drag & drop to reorder
            </Typography>
          </Col>

          <Col>
            <div className={alignRight}>
              <input
                type="file"
                accept="image/jpeg,image/png"
                ref={uploadInputRef}
                onChange={(ev) =>
                  ev.target.files && handleAddSlides(ev.target.files)
                }
                hidden
                multiple
                disabled={isLoading}
              />
              <Button
                color="primary"
                variant="text"
                size="medium"
                disabled={isLoading}
                onClick={() => {
                  if (uploadInputRef.current) {
                    uploadInputRef.current.click();
                  }
                }}
              >
                {isLoading ? 'Saving...' : 'Upload'}
              </Button>
            </div>
          </Col>
        </Row>

        {errorMessage && (
          <Row>
            <Typography variant="body" size="small" color="ERROR-100">
              {errorMessage}
            </Typography>
          </Row>
        )}

        <Row>
          <Col>
            {orderedSlides.length === 0 && !isLoading ? (
              <Placeholder
                onClick={() => {
                  if (uploadInputRef.current) {
                    uploadInputRef.current.click();
                  }
                }}
              />
            ) : (
              <div className={s.container}>
                <div className={s.scrollContainer}>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={orderedSlides.map((slide) => slide.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className={s.templatesList}>
                        {orderedSlides.map((slide) => (
                          <SortableTemplateItem
                            key={slide.id}
                            id={slide.id}
                            isUploading={isLoading}
                            onRemove={
                              !isLoading
                                ? (id) => extras.dispatch('RemoveSlide', id)
                                : undefined
                            }
                          />
                        ))}
                      </div>
                    </SortableContext>
                    <DragOverlay>
                      {activeId ? <DragOverlayItem id={activeId} /> : null}
                    </DragOverlay>
                  </DndContext>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Col>
    </DragDropWrapper>
  );
};

const Placeholder = (p: { onClick: () => void }) => {
  return (
    <div className={s.placeholder} onClick={p.onClick}>
      <div className={s.placeholderContent}>
        <div className={s.placeholderIcon}>
          <FontAwesomeIcon icon={faFiles} size="2x" />
        </div>
        <Typography variant="body" size="large" color={'N-80'}>
          Click to browse or
          <br />
          drag and drop your file.
        </Typography>
      </div>
    </div>
  );
};
